/*
 * ImageZoom Web Component
 *
 * This custom element provides an interactive image zoom and panning feature. 
 * It allows users to click or tap an image to toggle a zoomed view, where the image is 
 * displayed as a background with a configurable zoom level (default: 2x). 
 * The zoomed image pans smoothly based on mouse or touch movement, ensuring 
 * the image edges align with the container edges without overflow.
 *
 * Key Features:
 * - Supports any image size and screen resolution.
 * - Configurable zoom level via the 'zoom' attribute (e.g., zoom="1.5" for 150% zoom).
 * - Smooth opacity transition for the image when toggling zoom state.
 * - Optimized event handling with debouncing for performance during rapid mouse/touch movements.
 * - Prevents overflow with a buffer zone for precise panning.
 * - Handles dynamic image loading for Shopify's img_url filter.
 * - Stable single-tap detection (<400ms, <5px movement) for zoom mode toggle in modal slider.
 * - Prevents modal closing on mobile by blocking click and pointerup events during zoom toggle.
 * - Allows touch events to propagate to SliderComponent when not in panning mode for slide navigation.
 * - Applies larger zoom level on mobile devices for better visibility.
 *
 * Usage:
 * <image-zoom src="image.jpg" alt="Description" zoom="2"></image-zoom>
 */

class ImageZoom extends HTMLElement {
  constructor() {
    super();
    // Initialize constants and state
    this.buffer = 10; // Buffer zone for smoother panning near edges
    this.isPanning = false; // Tracks whether the image is in zoomed/panning state
    this.lastMoveTime = 0; // Timestamp for debouncing mouse/touch events
    this.moveTimeout = null; // Timeout for debounced panning
    this.touchStartTime = 0; // Timestamp for touchstart to detect single tap
    this.isPendingTap = false; // Tracks if a tap is being evaluated
    this.touchMoved = false; // Tracks if touchmove occurred to prevent accidental taps
    this.touchStartX = 0; // X coordinate of touchstart for movement detection
    this.touchStartY = 0; // Y coordinate of touchstart for movement detection
    this.touchStartMouseX = 0; // New: Store touchstart mouseX
    this.touchStartMouseY = 0; // New: Store touchstart mouseY

    // Create container element
    this.container = document.createElement('div');
    this.container.className = 'image-container';

    // Create image element
    this.img = document.createElement('img');
    this.img.src = this.getAttribute('src') || ''; // Set image source from attribute
    this.img.alt = this.getAttribute('alt') || 'Image'; // Set alt text from attribute
    this.img.onload = () => this.initializeZoom(); // Initialize zoom after image loads
    this.container.appendChild(this.img);

    // Attach event listeners for interaction
    this.container.addEventListener('click', (e) => this.handleEvent(e));
    this.container.addEventListener('touchstart', (e) => this.handleEvent(e));
    this.container.addEventListener('touchend', (e) => this.handleEvent(e));
    this.container.addEventListener('mousemove', (e) => this.handleEvent(e));
    this.container.addEventListener('touchmove', (e) => this.handleEvent(e));
    // Add click event listener to prevent modal closing
    this.container.addEventListener('click', (e) => {
      if (this.isPendingTap || this.isPanning) {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    // Append container to the component
    this.appendChild(this.container);
  }

  // Ensure component is fully connected to handle Dawn theme section reloads
  connectedCallback() {
    this.initializeZoom(); // Re-initialize zoom on section load
  }

  // Initialize zoom level from the 'zoom' attribute
  initializeZoom() {
    let zoom = parseFloat(this.getAttribute('zoom')) || 2; // Default to 2x if not specified
    // Increase zoom level on mobile devices for better visibility
    const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      zoom *= 1.4; // Increase zoom by 1.4x on mobile (e.g., 1.5x becomes ~2.1x)
    }
    if (zoom > 0) { // Validate zoom value
      this.container.style.setProperty('--zoom', `${zoom * 100}%`);
    }
  }

  // Define attributes to observe for changes
  static get observedAttributes() {
    return ['zoom', 'src', 'alt'];
  }

  // Handle attribute changes
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'zoom') {
      const zoom = parseFloat(newValue);
      if (zoom > 0) {
        this.container.style.setProperty('--zoom', `${zoom * 100}%`);
      }
    } else if (name === 'src') {
      this.img.src = newValue;
    } else if (name === 'alt') {
      this.img.alt = newValue;
    }
  }

  // Calculate mouse/touch coordinates relative to the container
  getMouseCoordinates(event) {
    const containerRect = this.container.getBoundingClientRect();
    const clientX = event.clientX || (event.touches && event.touches[0].clientX);
    const clientY = event.clientY || (event.touches && event.touches[0].clientY);
    const mouseX = clientX - containerRect.left;
    const mouseY = clientY - containerRect.top;
    return { mouseX, mouseY };
  }

  // Centralized event handler for all interactions
  handleEvent(event) {
    const isInModal = this.closest('.product-media-modal'); // Check if in modal context
    if (event.type === 'click' && isInModal) {
      // Handle desktop click to toggle panning mode in modal
      this.togglePanning(event);
    } else if (event.type === 'touchstart' && event.touches.length === 1) {
      // Handle touchstart for single tap detection
      this.isPendingTap = true;
      this.touchMoved = false;
      this.touchStartTime = performance.now();
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      // Store touchstart coordinates
      const { mouseX, mouseY } = this.getMouseCoordinates(event);
      this.touchStartMouseX = mouseX;
      this.touchStartMouseY = mouseY;
      if (this.isPanning) {
        event.preventDefault(); // Prevent default only in panning mode
        event.stopPropagation(); // Block slider events in panning mode
      }
      if (!isInModal) {
        // Allow modal opening in product-media-gallery
        return;
      }
      // Do not block touchstart for slider navigation when not in panning mode
    } else if (event.type === 'touchend' && this.isPendingTap && isInModal) {
      // Handle touchend to confirm single tap in modal
      const touchDuration = performance.now() - this.touchStartTime;

      if (touchDuration < 400 && !this.touchMoved) { // Consider <400ms and no movement as a single tap
        event.preventDefault(); // Prevent default to avoid modal closing
        event.stopPropagation(); // Prevent slider and modal events
        // Pass touchstart coordinates to togglePanning
        this.togglePanning({ mouseX: this.touchStartMouseX, mouseY: this.touchStartMouseY });
      }
      this.isPendingTap = false;
      this.touchMoved = false;
    } else if (event.type === 'mousemove' && this.isPanning) {
      // Handle mousemove for desktop panning
      const now = performance.now();
      if (now - this.lastMoveTime > 16) { // Limit to ~60fps
        this.lastMoveTime = now;
        this.panImage(event);
      } else {
        clearTimeout(this.moveTimeout);
        this.moveTimeout = setTimeout(() => this.panImage(event), 16);
      }
    } else if (event.type === 'touchmove' && this.isPanning) {
      // Handle touchmove for mobile panning in zoom mode
      event.preventDefault(); // Prevent browser scrolling
      event.stopPropagation(); // Prevent slider events
      const now = performance.now();
      if (now - this.lastMoveTime > 16) { // Limit to ~60fps
        this.lastMoveTime = now;
        this.panImage(event);
      } else {
        clearTimeout(this.moveTimeout);
        this.moveTimeout = setTimeout(() => this.panImage(event), 16);
      }
    } else if (event.type === 'touchmove' && this.isPendingTap) {
      // Detect movement to cancel tap if dragging
      const clientX = event.touches[0].clientX;
      const clientY = event.touches[0].clientY;
      const moveDistance = Math.sqrt(
        Math.pow(clientX - this.touchStartX, 2) + Math.pow(clientY - this.touchStartY, 2)
      );
      if (moveDistance > 5) { // Consider >5px movement as drag, not tap
        this.touchMoved = true;
      }
    }
  }

  // Toggle between zoomed/panning and normal states
  togglePanning(event) {
    this.isPanning = !this.isPanning;
    if (this.isPanning) {
      this.container.classList.add('panning');
      this.container.style.backgroundImage = `url(${this.img.src})`;
      // Use provided coordinates or default to center
      const mouseX = typeof event.mouseX === 'number' ? event.mouseX : this.container.getBoundingClientRect().width / 2;
      const mouseY = typeof event.mouseY === 'number' ? event.mouseY : this.container.getBoundingClientRect().height / 2;
      this.panImage({ mouseX, mouseY });
    } else {
      this.container.classList.remove('panning');
      this.container.style.backgroundImage = 'none';
    }
  }

  // Update background position based on mouse/touch coordinates
  panImage(event) {
    let mouseX = event.mouseX;
    let mouseY = event.mouseY;
    if (typeof mouseX !== 'number' || typeof mouseY !== 'number') {
      const coords = this.getMouseCoordinates(event);
      mouseX = coords.mouseX;
      mouseY = coords.mouseY;
    }

    const containerRect = this.container.getBoundingClientRect();
    const xPercentage = Math.max(0, Math.min(1, (mouseX - this.buffer) / (containerRect.width - this.buffer * 2))) * 100;
    const yPercentage = Math.max(0, Math.min(1, (mouseY - this.buffer) / (containerRect.height - this.buffer * 2))) * 100;

    this.container.style.backgroundPosition = `${xPercentage}% ${yPercentage}%`;
  }
}

customElements.define('image-zoom', ImageZoom);